import React, { useState, useRef, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import SearchInput from "./SearchInput";
import WorkspaceRow from "./WorkspaceRow";
import UserRow from "./UserRow";
import TeamRow from "./TeamRow";
import PlayerRow from "./PlayerRow";
import useInfiniteScroll from "v2/hooks/useInfiniteScroll";
import betaAPI from "v2/api";

// Labels are translated at render time: admin.superUser.tabs.<id>.label
const TABS = [
  { id: "workspaces" },
  { id: "users" },
  { id: "teams" },
  { id: "players" },
];

const TAB_FETCH_FNS = {
  workspaces: betaAPI.getAdminWorkspaces,
  users: betaAPI.getAdminUsers,
  teams: betaAPI.getAdminTeams,
  players: betaAPI.getAdminPlayers,
};

const AdminTabbedSection = ({ counts = {} }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("workspaces");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [tabCounts, setTabCounts] = useState({});
  const tabsRef = useRef({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Update indicator position when active tab changes
  useEffect(() => {
    const activeTabEl = tabsRef.current[activeTab];
    if (activeTabEl) {
      setIndicatorStyle({
        left: activeTabEl.offsetLeft,
        width: activeTabEl.offsetWidth,
      });
    }
  }, [activeTab, tabCounts]);

  // Tab badges come from the single /overview/ call (passed in as `counts`);
  // no need to hit each list endpoint just for a total. `athletes` in the
  // overview maps to the "players" tab.
  useEffect(() => {
    setTabCounts({
      workspaces: counts.workspaces ?? 0,
      users: counts.users ?? 0,
      teams: counts.teams ?? 0,
      players: counts.athletes ?? 0,
    });
  }, [counts]);

  // Get fetch function based on active tab
  const fetchFn = useMemo(
    () => TAB_FETCH_FNS[activeTab] || TAB_FETCH_FNS.workspaces,
    [activeTab]
  );

  const { items, loading, hasMore, totalCount, lastItemRef, refresh } =
    useInfiniteScroll({
      fetchFn,
      search: debouncedSearch,
      pageSize: 20,
    });

  // Keep tabCounts in sync with active tab's live totalCount
  useEffect(() => {
    if (totalCount > 0) {
      setTabCounts((prev) => ({ ...prev, [activeTab]: totalCount }));
    }
  }, [activeTab, totalCount]);

  // Reset when tab changes
  useEffect(() => {
    refresh();
  }, [activeTab, refresh]);

  // Render appropriate row component based on active tab
  const renderRow = (item, index, isLast) => {
    const ref = isLast ? lastItemRef : null;

    switch (activeTab) {
      case "workspaces":
        return (
          <WorkspaceRow
            key={item.id}
            workspace={item}
            isLast={isLast}
            ref={ref}
          />
        );
      case "users":
        return (
          <UserRow
            key={item.id}
            user={item}
            isLast={isLast}
            ref={ref}
            onDeleted={refresh}
          />
        );
      case "teams":
        return <TeamRow key={item.id} team={item} isLast={isLast} ref={ref} />;
      case "players":
        return (
          <PlayerRow key={item.id} player={item} isLast={isLast} ref={ref} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header with tabs and search */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4">
        {/* Tab Buttons */}
        <div className="relative flex">
          {/* Sliding indicator */}
          <div
            className="absolute bottom-0 h-0.5 bg-[#E07A3A] transition-all duration-300 ease-out"
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
            }}
          />
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                ref={(el) => (tabsRef.current[tab.id] = el)}
                onClick={() => setActiveTab(tab.id)}
                className="relative px-4 py-3.5 text-sm font-semibold transition-colors duration-200"
                style={{
                  color: isActive ? "#E07A3A" : "#6B7280",
                }}
              >
                {t(`admin.superUser.tabs.${tab.id}.label`)}
                {count > 0 && (
                  <span
                    className="ml-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium transition-colors duration-200"
                    style={{
                      backgroundColor: isActive ? "#FFF3ED" : "#F3F4F6",
                      color: isActive ? "#E07A3A" : "#6B7280",
                    }}
                  >
                    {count.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t(`admin.superUser.tabs.${activeTab}.searchPlaceholder`)}
        />
      </div>

      {/* Scrollable List */}
      <div className="max-h-[600px] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {items.map((item, index) =>
              renderRow(item, index, index === items.length - 1)
            )}

            {/* Loading spinner */}
            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={20} className="animate-spin text-[#E07A3A]" />
              </div>
            )}

            {/* Empty state */}
            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm text-[#6B7280]">
                  {debouncedSearch
                    ? t(`admin.superUser.tabs.${activeTab}.emptySearch`, {
                        search: debouncedSearch,
                      })
                    : t(`admin.superUser.tabs.${activeTab}.empty`)}
                </p>
              </div>
            )}

            {/* End of list indicator */}
            {!loading && items.length > 0 && !hasMore && (
              <div className="py-3 text-center text-xs text-[#9CA3AF]">
                {t(`admin.superUser.tabs.${activeTab}.showingAll`, {
                  total: totalCount.toLocaleString(),
                })}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminTabbedSection;
